import React from 'react';
import PropTypes from 'prop-types';
import { BellOff } from 'lucide-react';
import '../../styles/SmsOffBadge.css';

/**
 * SmsOffBadge — "this member's SMS is off, and here is why".
 *
 * One component for the three admin/member screens (Live Conversations list,
 * User Management modal, member profile). Driven by the backend's shared
 * `SmsConsentState` shape:
 *   { sms_consent, sms_opt_out: {at, reason, ...}, requires_start_text, sms_sender_number }
 *
 * Renders nothing while SMS is on (or the field is missing), so it is safe to
 * drop next to any phone number.
 */

const REASON_TEXT = {
  stop_keyword: (when) => `SMS off — member texted STOP${when}.`,
  twilio_21610: (when) => `SMS off — carrier reported the member unsubscribed (STOP)${when}.`,
  account_settings: (when) => `SMS off — turned off in account settings${when}.`,
  admin_user_management: (when) => `SMS off — turned off by an admin${when}.`,
};

const whenText = (at) => {
  if (!at) return '';
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  return ` on ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

/** Plain-string reason, reusable for tooltips elsewhere (e.g. the reply box). */
export const smsOffReasonText = (sms) => {
  if (!sms || sms.sms_consent !== false) return '';
  const rec = sms.sms_opt_out;
  if (!rec || !rec.reason) return 'SMS off.';
  const build = REASON_TEXT[rec.reason];
  return build ? build(whenText(rec.at)) : `SMS off${whenText(rec.at)}.`;
};

/** Twilio's rule: a STOP'd number stays blocked until the member texts START. */
export const smsStartHint = (sms) => {
  if (!sms || !sms.requires_start_text) return '';
  return `Text START to ${sms.sms_sender_number || 'the Concierge number'} to finish turning texts back on.`;
};

/**
 * What the viewer can DO about it (plan §9). Twilio cannot be un-STOPped by us,
 * so after a STOP only the member's own START text helps; after a web/admin
 * opt-out the switch above turns it back on.
 */
export const smsOffActionText = (sms, audience = 'member') => {
  if (!sms || sms.sms_consent !== false) return '';
  const number = sms.sms_sender_number || 'the Concierge number';
  if (sms.requires_start_text) {
    return audience === 'admin'
      ? `This member turned off texts by replying STOP. Only they can turn texts back on, by texting START to ${number}.`
      : `You turned off texts by replying STOP. Text START to ${number} from your phone to turn them back on.`;
  }
  return audience === 'admin'
    ? 'Text messages are off for this member. Turn on the switch above to send texts.'
    : 'Text messages are off. Turn on the switch above to receive texts.';
};

/** Hover text on the badge: what it means first, then why, then what would turn texts back on. */
export const smsOffTooltip = (sms) => {
  if (!sms || sms.sms_consent !== false) return '';
  return ["This member won't receive text messages from Concierge.", smsOffReasonText(sms), smsStartHint(sms)]
    .filter(Boolean).join(' ');
};

const SmsOffBadge = ({ sms, className = '' }) => {
  if (!sms || sms.sms_consent !== false) return null;
  const title = smsOffTooltip(sms);
  return (
    <span
      className={`badge badge-danger sms-off-badge ${className}`.trim()}
      title={title}
      data-testid="sms-off-badge"
    >
      <BellOff size={12} aria-hidden="true" />SMS off
    </span>
  );
};

SmsOffBadge.propTypes = {
  sms: PropTypes.shape({
    sms_consent: PropTypes.bool,
    sms_opt_out: PropTypes.object,
    requires_start_text: PropTypes.bool,
    sms_sender_number: PropTypes.string,
  }),
  className: PropTypes.string,
};

export default SmsOffBadge;
